import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { revenueActions } from '@nxsys/store/slices/revenueFilings/reducer/revenueSlice';
import {
  GetPayrollToSubmitPSRParams,
  Payroll,
} from '@nxsys/store/slices/revenueFilings/types';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ActionsHeader } from './ActionsHeader';
import { getColumns } from './columns';
import { PSR_SUBMIT_TYPE, psrPayrollScope } from '@nxsys/constants/psr';
import SubmissionDetailModal from './SubmissionDetailModal';
import { showToast } from '@nxsys/utils/toast';
import { resolveFilterCompanyIds } from '@nxsys/utils/companyScope';

type Props = {
  cbAfterAction?: () => void;
};

const NormalTab: React.FC<Props> = ({ cbAfterAction }) => {
  const { messages } = useTypedIntl();

  // Revenue's tax year runs from January to December.
  const taxYear = new Date().getFullYear();

  const dispatch = useDispatch<AppDispatch>();
  const { payrollToSubmitPSR } = useSelector(
    (state: AppState) => state.revenueStore,
  );
  const { loading, list, pagination, queryParams } = payrollToSubmitPSR;
  const { isBureau, isBureauEmployer, user } = useSelector(
    (state: AppState) => state.appStore,
  );

  const [selectedRows, setSelectedRows] = useState<Payroll[]>([]);
  const [submissionDetailModal, setSubmissionDetailModal] = useState({
    open: false,
  });

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      taxYear,
      ...psrPayrollScope(isBureau),
      isNeedSubmitPSR: true,
    });
  }, [isBureau, taxYear]);

  useEffect(() => {
    const newSelectedRow: Payroll[] = [];
    selectedRows.forEach((item) => {
      const index = list.findIndex((i) => i.id === item.id);
      if (index !== -1) {
        newSelectedRow.push(list[index]);
      }
    });
    setSelectedRows(newSelectedRow);
  }, [list]);

  const handleFetchData = (params: GetPayrollToSubmitPSRParams) => {
    params.companyRefs = resolveFilterCompanyIds(
      params.companyRefs,
      isBureauEmployer,
      user?.companyUserIds,
    );
    dispatch(revenueActions.getPayrollToSubmitPSR(params));
  };

  const refetchData = () => {
    handleFetchData({ ...queryParams });
    cbAfterAction?.();
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const toggleSubmissionDetail = () =>
    setSubmissionDetailModal({
      ...submissionDetailModal,
      open: !submissionDetailModal.open,
    });

  const handleNextStep = () => {
    if (selectedRows.length === 0) {
      showToast({
        type: 'warning',
        message: messages['common.selectAtLeastOneToSubmit'],
      });
      return;
    }

    toggleSubmissionDetail();
  };

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.payFrequency ||
    !!queryParams?.taxPeriod ||
    !!queryParams?.companyRefs;

  return (
    <>
      <AppsHeader>
        <ActionsHeader
          onFilter={handleFetchData}
          type={PSR_SUBMIT_TYPE.NORMAL}
          onNext={handleNextStep}
        />
      </AppsHeader>
      <AppsContent>
        <AppTableContainer
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 2000, y: 700 }}
          titleEmpty={messages['psr.submit.empty']}
          columns={getColumns({
            messages,
            psrSubmitType: PSR_SUBMIT_TYPE.NORMAL,
          })}
          onChangePagination={onChangePagination}
          rowSelection={{
            type: 'checkbox',
            onChange: (_: React.Key[], selectedRows: any[]) => {
              setSelectedRows(selectedRows);
            },
          }}
          noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
        />
      </AppsContent>
      <SubmissionDetailModal
        isOpen={submissionDetailModal.open}
        toggle={toggleSubmissionDetail}
        data={selectedRows}
        refetchData={refetchData}
      />
    </>
  );
};

export default NormalTab;
